import { Box, useMediaQuery, useTheme } from '@mui/material';
import { Breakpoint } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import clsx from 'clsx';
import { usePageControlsStyles } from './page-controls.styles';
import { TextField } from '../text-field/text-field.component';
import { Dropdown } from '../dropdown/dropdown.component';
import { MultiSelectDropdown } from '../dropdown/multi-select-dropdown.component';
import { ErrorAlert } from '../errors/error-alert/error-alert.component';
import { Autocomplete } from '@mui/material';
import { useTextFieldStyles } from '../text-field/text-field.styles';

type PageControlsContextValue = {
  form: { [key: string]: any };
  onFormInput: (update: any) => void;
};

// Context is only used internally and should not be exported
const PageControlsContext = createContext<PageControlsContextValue>(undefined!);

// Creates a stateful context wrapper that provides values and handlers to inputs
export function PageControlsWrapper<T extends object>({
  form,
  onFormInput,
  children
}: {
  form: T;
  onFormInput: (update: Partial<T>) => void;
  children: ReactNode;
}) {
  return <PageControlsContext.Provider value={{ form, onFormInput }}>{children}</PageControlsContext.Provider>;
}

// Creates a wrapper for inputs to be treated as advanced on mobile view
export function AdvancedControls({
  children,
  advancedBreakpoint,
  hasActiveInputs
}: {
  children: ReactNode;
  advancedBreakpoint: Breakpoint;
  hasActiveInputs: boolean;
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [touched, setTouched] = useState(false);
  const theme = useTheme();
  const isWithinBreakpoint = useMediaQuery(theme.breakpoints.down(advancedBreakpoint));
  const classes = usePageControlsStyles();

  // Detect if advanced inputs are active on page load, and set to open if so
  useEffect(() => {
    if (!showAdvanced && !touched && hasActiveInputs) {
      setShowAdvanced(true);
    }
  }, [showAdvanced, touched, hasActiveInputs]);

  if (!isWithinBreakpoint) return <>{children}</>;
  return (
    <>
      {showAdvanced ? children : null}
      <Box
        className={classes.filtersToggle}
        onClick={() => {
          setShowAdvanced(!showAdvanced);
          setTouched(true);
        }}
      >
        {showAdvanced ? 'Hide' : 'Show'} advanced filters
      </Box>
    </>
  );
}

// The following inputs are wrappers around standard inputs that
// extract values and handlers from the page controls context
type ForwardedProps<T extends (...args: any) => any> = Omit<
  Parameters<T>[0],
  'onChange' | 'value' | 'name' | 'dropdownVariant' | 'textFieldVariant'
>;

export function PageControlSearchInput({ name, ...rest }: { name: string } & ForwardedProps<typeof TextField>) {
  const { form, onFormInput } = useContext(PageControlsContext);
  return (
    <TextField
      textFieldVariant="secondarySolid"
      textColorOverride="white"
      value={form[name]}
      shrinkLabel={false}
      onChange={(e) => {
        onFormInput({ [name]: e.target.value });
      }}
      name={name}
      {...rest}
    />
  );
}

export function PageControlDropdown({ name, ...rest }: { name: string } & ForwardedProps<typeof Dropdown>) {
  const { form, onFormInput } = useContext(PageControlsContext);
  return (
    <Dropdown
      dropdownVariant="secondarySolid"
      textColorOverride="white"
      shrinkLabel={false}
      value={form[name]}
      name={name}
      onChange={(e) => onFormInput({ [name]: e.target.value })}
      {...rest}
    />
  );
}

export function PageControlAutoComplete({
  name,
  placeholder,
  onOpen,
  loading,
  options
}: {
  name: string;
  placeholder?: string;
  options: { label: string; value: string }[];
  onOpen?: () => void;
  loading?: boolean;
}) {
  const { form, onFormInput } = useContext(PageControlsContext);
  const classes = useTextFieldStyles({ variant: 'secondarySolid' });
  type Option = { label: string; value: string };
  const selectedOption = options.find((o) => o.value === form[name]) ?? null;
  return (
    <Autocomplete<Option>
      // If the value is known before the list of options has loaded, the Autocomplete component does not rerender when the options change.
      // To force the Autocomplete component to rerender, we make its key the label of the selected option.
      key={selectedOption?.label || 'autocomplete-input'}
      value={selectedOption}
      onChange={(_, selected) => onFormInput({ [name]: selected?.value || null })}
      options={options}
      onOpen={onOpen}
      loading={loading}
      blurOnSelect
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      style={{
        color: 'white'
      }}
      classes={{
        popupIndicator: classes.dropdownIcon,
        clearIndicator: classes.dropdownIcon
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          value={form[name]}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              className: classes.textField,
              style: { paddingRight: '40px' }
            },
            inputLabel: {
              ...params.slotProps.inputLabel,
              className: classes.textFieldLabel,
              shrink: false
            }
          }}
          textFieldVariant="secondarySolid"
          shrinkLabel={false}
          textColorOverride="white"
          placeholder={placeholder || ''}
        />
      )}
    />
  );
}

export function MultiSelectPageControlDropdown<T extends string | number>({
  name,
  showErrorOnEmptyValue,
  menuItems,
  ...rest
}: {
  name: string;
  showErrorOnEmptyValue: boolean;
  menuItems: { label: string; imageURL?: string | undefined; value: T }[];
} & ForwardedProps<typeof MultiSelectDropdown>) {
  const { form, onFormInput } = useContext(PageControlsContext);
  const [error, setError] = useState(false);
  return (
    <>
      {showErrorOnEmptyValue && error === true ? <ErrorAlert open={true} message="Please select at least one" /> : null}
      <MultiSelectDropdown
        dropdownVariant="secondarySolid"
        textColorOverride="white"
        shrinkLabel={false}
        value={form[name]}
        multiple={true}
        textFieldDesktopWidth="300px"
        onChange={(value) => {
          if (value.length > 0) {
            setError(false);
            return onFormInput({ [name]: value.join(',') });
          }
          setError(true);
        }}
        error={showErrorOnEmptyValue && error}
        helperText={showErrorOnEmptyValue && error ? 'Please select at least one item' : ''}
        menuItems={menuItems}
        {...rest}
      />
    </>
  );
}

export function PageControlDatePicker({
  name,
  dateTransformer,
  value,
  onChange,
  slots,
  slotProps,
  ...rest
}: {
  name: string;
  dateTransformer?: (value: Dayjs | null) => Dayjs | null;
  value?: Dayjs | null;
  onChange?: (value: Dayjs | null) => void;
  label?: string;
  format?: string;
  fullWidth?: boolean;
  disableFuture?: boolean;
  slots?: Record<string, any>;
  slotProps?: Record<string, any>;
}) {
  const classes = usePageControlsStyles();
  const { form, onFormInput } = useContext(PageControlsContext);
  return (
    <DatePicker
      className={clsx(classes.datePicker, ...[form[name] ? classes.datePickerInUse : undefined])}
      value={(value ?? form[name]) as Dayjs | null}
      onChange={(date) => {
        onChange?.(date);
        onFormInput({ [name]: dateTransformer ? dateTransformer(date) : date });
      }}
      name={name}
      slots={slots}
      slotProps={{
        // Keep the calendar above the fixed app bar / page chrome.
        popper: { style: { zIndex: 1500 } },
        ...slotProps
      }}
      {...rest}
    />
  );
}
